// License summary block. One-time / Ed25519 model — NO expiry, NO renewal. Shows
// the owned tier, its entitlement badges, and the machine binding (paid tiers are
// machine-bound; the anti-share boundary). `revoked` covers chargeback/fraud;
// otherwise a paid entitlement is perpetual and covers every future update —
// patch, minor AND major — at no extra cost. The status line states facts only and
// never sells: sales reopened 2026-09-16 (DECISIONS D40), but the offer belongs to
// the purchase flow, not to a status line.
//
// The keyless line says only that the app is free to use. Until desktop 2.29.6 the
// shipped app locked Toolkit, AI Studio and Publish behind a "Pro" badge for a
// keyless user (`LicenseStatus::free()` handed the webview an empty feature list), so
// no module sentence was printed (D40 (8)). The fix shipped in 2.29.6 and the
// marketing pages state module availability again (2026-10-03); this status line
// stays short, and the export limits render beneath it on /license
// (KeylessExportLimits).

import { cx, dataClass, statusDotClass, type BadgeTone } from "@ahcut/ui/recipes";
import { Badge } from "@/components/ui/badge";

export interface LicenseStatusProps {
  tier: string; // tier label ("Pro"), or "Tanpa kunci lisensi" for an account without a key
  active?: boolean; // owns a valid (non-revoked) paid entitlement
  revoked?: boolean; // chargeback/fraud → key invalid
  features?: string[]; // humanized entitlement labels for badges
  machineId?: string | null; // bound machine (null = not yet activated)
}

export function LicenseStatus({
  tier,
  active = false,
  revoked = false,
  features,
  machineId,
}: LicenseStatusProps) {
  const dotTone: BadgeTone = revoked ? "danger" : active ? "ok" : "neutral";
  const statusText = revoked
    ? "Dicabut"
    : active
      ? "Aktif, berlaku untuk semua pembaruan ahcut"
      : "ahcut gratis dipakai tanpa kunci lisensi";

  return (
    <div data-slot="license-status" className="flex flex-col gap-2">
      <p className="font-display text-heading text-fg-0">{tier}</p>
      <p className="flex items-start gap-2 text-sm text-fg-1">
        <span aria-hidden="true" className={cx(statusDotClass(dotTone), "mt-[0.4375rem]")} />
        {statusText}
      </p>
      {/* Only the machine id itself is data (mono); the label and the unbound note are prose. */}
      {machineId != null ? (
        <p className="text-sm text-fg-2">
          Perangkat: <span className={cx(dataClass, "break-all")}>{machineId}</span>
        </p>
      ) : (
        active && <p className="text-sm text-fg-2">Belum diaktivasi ke perangkat</p>
      )}
      {features && features.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {features.map((f) => (
            <Badge key={f} tone="pro">
              {f}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
